import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { apiClient } from 'api/ApiClient';
import type { UpdateCompanyRequest } from 'api/types';
import { useAuth } from 'shared/lib/auth/useAuth';
import { authDebug } from 'shared/lib/debug/authDebugger';

export enum CompanyQueryKeys {
  getTenantCompanies = 'getTenantCompanies',
  getCompanySettings = 'getCompanySettings',
  updateCompanySettings = 'updateCompanySettings',
}

export function useGetTenantCompanies() {
  const { isAuthReady } = useAuth();

  return useQuery({
    queryKey: [CompanyQueryKeys.getTenantCompanies],
    queryFn: () => {
      authDebug.log('useGetTenantCompanies', 'Executing /v1/companies/ API call');
      return apiClient.getTenantCompanies();
    },
    enabled: isAuthReady, // Only run when auth is ready
  });
}

export function useGetCompanySettings() {
  const { user } = useAuth();
  const id = user?.company?.id;

  return useQuery({
    queryKey: [CompanyQueryKeys.getCompanySettings, id],
    queryFn: () => (id ? apiClient.getCompanySettings(id) : Promise.reject()),
    enabled: !!id,
  });
}

export function useUpdateCompanySettings() {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const client = useQueryClient();
  const id = user?.company?.id;

  const { mutate, isPending } = useMutation({
    mutationKey: [CompanyQueryKeys.updateCompanySettings],
    mutationFn: (data: UpdateCompanyRequest) => (id ? apiClient.updateCompanySettings(id, data) : Promise.reject()),
    onMutate: () =>
      client.invalidateQueries({
        queryKey: [CompanyQueryKeys.getCompanySettings],
      }),
    onSuccess: (data) => {
      enqueueSnackbar('Your changes have been saved');

      client.setQueryData([CompanyQueryKeys.getCompanySettings, id], () => {
        return data;
      });
    },
  });

  return {
    update: mutate,
    isLoading: isPending,
  };
}
